import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import { EditorRole, DATE_FORMATS } from '@callmart/types';
import { useTranslation } from 'react-i18next';
import { appTimezone } from '../../config';
import dayjs from '@utils/dayjs';

export interface VersionEntry {
    userId: number;
    role: EditorRole;
    value: number;
    previousValue?: number | null;
    date: string;
    draft?: boolean;
}

interface CorrectedTotalsHistoryProps {
    versions: VersionEntry[];
    releasedValue?: number | null;
    draftValue?: number | null;
}

function CorrectedTotalsHistory({ versions, releasedValue, draftValue }: CorrectedTotalsHistoryProps) {
    const { t } = useTranslation();
    // Check if we have no versions and no values
    const hasNoVersions = !versions || versions.length === 0;
    if (hasNoVersions && releasedValue == null && draftValue == null) {
        return <Typography variant="body2">{t('common.noChangesAvailable')}</Typography>;
    }

    // Display versions from newest to oldest, sorted by date (merges draft + released)
    const sortedVersions = versions
        ? [...versions].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
        : [];

    const getRoleColor = (role: EditorRole): string => {
        return role === 'TEF' ? '#66BB6A' : '#FFA500';
    };

    return (
        <>
            <Typography variant="h6" style={{ textAlign: 'center', marginBottom: '.5em', paddingTop: '.5em' }}>
                {t('field.changes')}
            </Typography>

            {/* Version history entries (merged draft + released) */}
            {sortedVersions.length > 0 && (
                <>
                    {sortedVersions.map((version, index) => (
                        <div key={index} style={{ marginBottom: index < sortedVersions.length - 1 ? '8px' : 0 }}>
                            <Typography variant="subtitle1" textAlign={'left'} sx={{ paddingX: 2 }}>
                                <code style={{ color: 'var(--grey)' }}>
                                    {dayjs.utc(version.date).tz(appTimezone).format(DATE_FORMATS.DATETIME_DISPLAY)}
                                    :{' '}
                                </code>
                                <span style={{ color: getRoleColor(version.role), fontWeight: 600 }}>
                                    {version.role}
                                </span>
                                <Chip
                                    label={version.draft ? t('status.draft') : t('status.released')}
                                    size="small"
                                    color={version.draft ? 'warning' : 'success'}
                                    sx={{ marginX: 1, height: '20px', fontSize: '0.7rem' }}
                                />
                                {/* Show previous → current value change */}
                                {version.previousValue != null ? (
                                    <span style={{ marginLeft: '0.5em' }}>
                                        <span style={{ color: 'var(--grey)', textDecoration: 'line-through' }}>
                                            {version.previousValue}
                                        </span>
                                        <span style={{ color: 'var(--grey)' }}> → </span>
                                        <span style={{ fontWeight: 500 }}>{version.value}</span>
                                    </span>
                                ) : (
                                    <span style={{ marginLeft: '0.5em', fontWeight: 500 }}>{version.value}</span>
                                )}
                            </Typography>
                        </div>
                    ))}
                </>
            )}

            {/* Simple footer showing current values */}
            {(releasedValue != null || draftValue != null) && (
                <Typography
                    variant="subtitle1"
                    textAlign={'left'}
                    sx={{
                        paddingX: 2,
                        marginTop: '1em',
                        fontWeight: 'bold',
                        borderTop: '1px solid #eee',
                        paddingTop: '0.5em',
                    }}
                >
                    {releasedValue != null && (
                        <>
                            <span style={{ color: 'var(--grey)' }}>Released: </span>
                            <span style={{ color: 'var(--primary)' }}>{releasedValue}</span>
                        </>
                    )}
                    {releasedValue != null && draftValue != null && <span style={{ color: 'var(--grey)' }}> | </span>}
                    {draftValue != null && (
                        <>
                            <span style={{ color: 'var(--grey)' }}>Draft: </span>
                            <span style={{ color: '#FFA500' }}>{draftValue}</span>
                        </>
                    )}
                </Typography>
            )}
        </>
    );
}

export default CorrectedTotalsHistory;
